import { SimpleModal, Box } from '@sequencehq/core-components'
import { FC } from 'react'
import { useCustomer } from '../../../hooks/useCustomer'
import { useCustomerContactsContext } from 'CustomerContacts/communication/internal/CustomerContacts.domain.context'
import {
  ContactModalForm,
  CustomerContactsRoot,
  useContactModalForm
} from 'CustomerContacts/index'
import { GreyGrey10 } from '@sequencehq/core-components'
import { Label } from '@sequencehq/core-components'
import { CustomerContextProvider } from 'Customer/hooks/useCustomerContext'

type ConfirmStartScheduleModalProps = {
  onClose: () => void
  onConfirm: () => Promise<void>
  submitting: boolean
}

/**
 * The modal is visible for as long as it is mounted: it shows the submitting
 * spinner while `onConfirm` runs and the parent unmounts it on completion.
 */
export const ConfirmStartScheduleModal: FC<
  ConfirmStartScheduleModalProps
> = props => {
  const { customer } = useCustomer()

  /**
   * This isn't the most ideal handling - but given this
   * is cached data it isn't the worst. This multi-modal
   * deal with the old approach could do with a cleanup,
   * at which point we can easily set the V2 modal to
   * be loading.
   */
  if (!customer) {
    return null
  }

  return customer.contacts?.length ? (
    <RequirementsMetContent
      onCancel={props.onClose}
      onConfirm={() => void props.onConfirm()}
      submitting={props.submitting}
    />
  ) : (
    <CustomerContextProvider customerId={customer?.id}>
      <CustomerContactsRoot customerId={customer?.id}>
        <ContactNeededContent
          onCancel={props.onClose}
          onConfirm={() => void props.onConfirm()}
          submitting={props.submitting}
        />
      </CustomerContactsRoot>
    </CustomerContextProvider>
  )
}

const RequirementsMetContent: FC<{
  onCancel: () => void
  onConfirm: () => void
  submitting: boolean
}> = ({ onCancel, onConfirm, submitting }) => {
  return (
    <SimpleModal
      variant="dialog"
      isOpen
      data-testid="schedule.confirmStart"
      title="Start schedule"
      onClose={onCancel}
      submitButtonText="Start schedule"
      submittingButtonText="Start schedule"
      submitting={submitting}
      disableSubmitOnEnter
      onSubmit={() => {
        onConfirm()
        return false
      }}
    >
      The schedule will start generating invoices based on the defined prices
      and billing dates.
    </SimpleModal>
  )
}

const ContactNeededContent: FC<{
  onCancel: () => void
  onConfirm: () => void
  submitting: boolean
}> = ({ onCancel, onConfirm, submitting }) => {
  const ctx = useCustomerContactsContext()

  const { fieldsConfig, buttonSubmit } = useContactModalForm({
    contactId: 'new',
    customerId: ctx.queries.data.customerId,
    fieldsValues: {
      ...ctx.queries.formData,
      purposes: ['BILLING'],
      notificationSubscriptions: ['BILLING']
    },
    updateFields: ctx.mutators.internal.updateForm,
    createContact: ctx.mutators.external.out.createContact,
    updateContact: ctx.mutators.external.out.updateContact
  })

  return (
    <SimpleModal
      variant="dialog"
      isOpen
      data-testid="schedule.confirmStart"
      title="Start schedule"
      onClose={onCancel}
      submitButtonText="Start schedule"
      submittingButtonText="Start schedule"
      submitting={submitting}
      disableSubmitOnEnter
      onSubmit={async () => {
        // Awaited so the contact is created (with the spinner showing) while
        // the modal is still mounted; `onConfirm` then drives the external
        // `submitting` state until the parent unmounts the modal.
        await buttonSubmit.onSubmit(onConfirm)
        return false
      }}
      submitDisabled={buttonSubmit.disabled}
    >
      You need to add a billing contact to start this schedule. After that, the
      schedule will start generating invoices based on the defined prices and
      billing dates.
      <Box background={GreyGrey10} borderRadius={6} p={4} mt={4}>
        <Label>Billing Contact</Label>
        <ContactModalForm fieldsConfig={fieldsConfig} hideLabels />
        <Box mt={-3} />
      </Box>
    </SimpleModal>
  )
}
